import { Suspense, useCallback, useEffect, useState } from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import * as Sentry from '@sentry/react';
import { ConfigProvider } from 'antd';
import getLocalStorageApi from 'api/browser/localstorage/get';
import setLocalStorageApi from 'api/browser/localstorage/set';
import logEvent from 'api/common/logEvent';
import AppLoading from 'components/AppLoading/AppLoading';
import { CmdKPalette } from 'components/cmdKPalette/cmdKPalette';
import NotFound from 'components/NotFound';
import { ShiftHoldOverlayController } from 'components/ShiftOverlay/ShiftHoldOverlayController';
import Spinner from 'components/Spinner';
import { FeatureKeys } from 'constants/features';
import { LOCALSTORAGE } from 'constants/localStorage';
import ROUTES from 'constants/routes';
import AppLayout from 'container/AppLayout';
import Hex from 'crypto-js/enc-hex';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import { KeyboardHotkeysProvider } from 'hooks/hotkeys/useKeyboardHotkeys';
import { useIsAIAssistantEnabled } from 'hooks/useIsAIAssistantEnabled';
import { useIsDarkMode, useThemeConfig } from 'hooks/useDarkMode';
import { useGetTenantLicense } from 'hooks/useGetTenantLicense';
import { NotificationProvider } from 'hooks/useNotifications';
import { ResourceProvider } from 'hooks/useResourceAttribute';
import { StatusCodes } from 'http-status-codes';
import history from 'lib/history';
import ErrorBoundaryFallback from 'pages/ErrorBoundaryFallback/ErrorBoundaryFallback';
import posthog from 'posthog-js';
import { useAppContext } from 'providers/App/App';
import { IUser } from 'providers/App/types';
import { CmdKProvider } from 'providers/cmdKProvider';
import { ErrorModalProvider } from 'providers/ErrorModalProvider';
import { PreferenceContextProvider } from 'providers/preferences/context/PreferenceContextProvider';
import { QueryBuilderProvider } from 'providers/QueryBuilder';
import { LicenseStatus } from 'types/api/licensesV3/getActive';
import { extractDomain } from 'utils/app';

import { Home } from './pageComponents';
import PrivateRoute from './Private';
import defaultRoutes, {
	AppRoutes,
	LIST_LICENSES,
	SUPPORT_ROUTE,
} from './routes';

function App(): JSX.Element {
	const themeConfig = useThemeConfig();
	const {
		user,
		isFetchingUser,
		isFetchingFeatureFlags,
		trialInfo,
		activeLicense,
		isFetchingActiveLicense,
		activeLicenseFetchError,
		userFetchError,
		featureFlagsFetchError,
		isLoggedIn: isLoggedInState,
		featureFlags,
		org,
		isPreflightLoading,
	} = useAppContext();
	const [routes, setRoutes] = useState<AppRoutes[]>(defaultRoutes);
	const isAIAssistantEnabled = useIsAIAssistantEnabled();

	const { hostname } = window.location;
	const [pathname, setPathname] = useState(history.location.pathname);

	const { isCloudUser, isEnterpriseSelfHostedUser } = useGetTenantLicense();

	const [isSentryInitialized, setIsSentryInitialized] = useState(false);

	useEffect(() => {
		return history.listen((location) => {
			setPathname(location.pathname);
		});
	}, []);

	const enableAnalytics = useCallback(
		(user: IUser): void => {
			// wait for the required data to be loaded before doing init for anything!
			if (!isFetchingActiveLicense && activeLicense && org) {
				const orgName =
					org && Array.isArray(org) && org.length > 0 ? org[0].displayName : '';

				const { displayName, email, role, id, orgId } = user;

				const domain = extractDomain(email);
				const hostNameParts = hostname.split('.');

				const identifyPayload = {
					email,
					name: displayName,
					company_name: orgName,
					deployment_name: hostNameParts[0],
					data_region: hostNameParts[1],
					deployment_url: hostname,
					company_domain: domain,
					source: 'signoz-ui',
					role,
				};

				const groupTraits = {
					name: orgName,
					deployment_name: hostNameParts[0],
					data_region: hostNameParts[1],
					deployment_url: hostname,
					company_domain: domain,
					source: 'signoz-ui',
				};

				if (email) {
					logEvent('Email Identified', identifyPayload, 'identify');
				}

				if (domain) {
					logEvent('Domain Identified', groupTraits, 'group');
				}
				if (window && window.Appcues) {
					window.Appcues.identify(id, {
						name: displayName,
						deployment_name: hostNameParts[0],
						data_region: hostNameParts[1],
						deployment_url: hostname,
						company_domain: domain,
						companyName: orgName,
						email,
						paidUser: !!trialInfo?.trialConvertedToSubscription,
					});
				}

				posthog?.identify(id, {
					email,
					name: displayName,
					orgName,
					deployment_name: hostNameParts[0],
					data_region: hostNameParts[1],
					deployment_url: hostname,
					company_domain: domain,
					source: 'signoz-ui',
					isPaidUser: !!trialInfo?.trialConvertedToSubscription,
				});

				posthog?.group('company', orgId, {
					name: orgName,
					deployment_name: hostNameParts[0],
					data_region: hostNameParts[1],
					deployment_url: hostname,
					company_domain: domain,
					source: 'signoz-ui',
					isPaidUser: !!trialInfo?.trialConvertedToSubscription,
				});
			}
		},
		[
			hostname,
			isFetchingActiveLicense,
			activeLicense,
			org,
			trialInfo?.trialConvertedToSubscription,
		],
	);

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (
			!isFetchingActiveLicense &&
			(activeLicense || activeLicenseFetchError) &&
			!isFetchingUser &&
			user &&
			!!user.email
		) {
			// either the active API returns error with 404 or 501 and if it returns a terminated license means it's on basic plan
			const isOnBasicPlan =
				(activeLicenseFetchError &&
					[StatusCodes.NOT_FOUND, StatusCodes.NOT_IMPLEMENTED].includes(
						activeLicenseFetchError?.getHttpStatusCode(),
					)) ||
				(activeLicense?.status && activeLicense.status === LicenseStatus.INVALID);
			const isIdentifiedUser = getLocalStorageApi(LOCALSTORAGE.IS_IDENTIFIED_USER);

			if (isLoggedInState && user && user.id && user.email && !isIdentifiedUser) {
				setLocalStorageApi(LOCALSTORAGE.IS_IDENTIFIED_USER, 'true');
			}

			let updatedRoutes = defaultRoutes;
			// if the user is a cloud user
			if (isCloudUser || isEnterpriseSelfHostedUser) {
				// if the user is on basic plan then remove billing
				if (isOnBasicPlan) {
					updatedRoutes = updatedRoutes.filter(
						(route) =>
							route?.path !== ROUTES.BILLING && route?.path !== ROUTES.INTEGRATIONS,
					);
				}

				if (isEnterpriseSelfHostedUser) {
					updatedRoutes.push(LIST_LICENSES);
				}

				// always add support route for cloud users
				updatedRoutes = [...updatedRoutes, SUPPORT_ROUTE];
			} else {
				// if not a cloud user then remove billing and add list licenses route
				updatedRoutes = updatedRoutes.filter(
					(route) =>
						route?.path !== ROUTES.BILLING && route?.path !== ROUTES.INTEGRATIONS,
				);
				updatedRoutes = [...updatedRoutes, LIST_LICENSES];
			}
			setRoutes(updatedRoutes);
		}
	}, [
		isLoggedInState,
		user,
		isCloudUser,
		isEnterpriseSelfHostedUser,
		isFetchingActiveLicense,
		isFetchingUser,
		activeLicense,
		activeLicenseFetchError,
	]);

	useEffect(() => {
		if (!isLoggedInState) {
			return;
		}

		setRoutes((prev) => {
			const hasAi = prev.some((r) => r.key === 'AI_ASSISTANT');
			if (isAIAssistantEnabled === hasAi) {
				return prev;
			}
			if (isAIAssistantEnabled) {
				const aiRoute = defaultRoutes.find((r) => r.key === 'AI_ASSISTANT');
				if (!aiRoute) {
					return prev;
				}
				return [...prev.filter((r) => r.key !== 'AI_ASSISTANT'), aiRoute];
			}
			return prev.filter((r) => r.key !== 'AI_ASSISTANT');
		});
	}, [isLoggedInState, isAIAssistantEnabled]);

	const isDarkMode = useIsDarkMode();

	useEffect(() => {
		window.Pylon?.('setTheme', isDarkMode ? 'dark' : 'light');
	}, [isDarkMode]);

	useEffect(() => {
		if (
			pathname === ROUTES.ONBOARDING ||
			pathname.startsWith('/public/dashboard/') ||
			pathname === '/ai-assistant' ||
			pathname.startsWith('/ai-assistant/')
		) {
			window.Pylon?.('hideChatBubble');
		} else {
			window.Pylon?.('showChatBubble');
		}
	}, [pathname]);

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		// feature flag shouldn't be loading and featureFlags or fetchError any one of this should be true indicating that req is complete
		// licenses should also be present. there is no check for licenses for loading and error as that is mandatory if not present then routing
		// to something went wrong which would ideally need a reload.
		if (
			!isFetchingFeatureFlags &&
			(featureFlags || featureFlagsFetchError) &&
			activeLicense &&
			trialInfo
		) {
			let isChatSupportEnabled = false;
			let isPremiumSupportEnabled = false;
			if (featureFlags && featureFlags.length > 0) {
				isChatSupportEnabled =
					featureFlags.find((flag) => flag.name === FeatureKeys.CHAT_SUPPORT)
						?.active || false;

				isPremiumSupportEnabled =
					featureFlags.find((flag) => flag.name === FeatureKeys.PREMIUM_SUPPORT)
						?.active || false;
			}
			const showAddCreditCardModal =
				!isPremiumSupportEnabled && !trialInfo?.trialConvertedToSubscription;

			if (
				isLoggedInState &&
				isChatSupportEnabled &&
				!showAddCreditCardModal &&
				(isCloudUser || isEnterpriseSelfHostedUser) &&
				window.signozBootData?.settings?.pylon?.enabled
			) {
				const email = user.email || '';
				const secret = window.signozBootData?.settings?.pylon?.identitySecret || '';
				let emailHash = '';

				if (email && secret) {
					emailHash = HmacSHA256(email, Hex.parse(secret)).toString(Hex);
				}

				window.pylon = {
					chat_settings: {
						app_id: window.signozBootData?.settings?.pylon?.appId,
						email: user.email,
						name: user.displayName || user.email,
						email_hash: emailHash,
					},
				};
			}
		}
	}, [
		isLoggedInState,
		user,
		pathname,
		trialInfo?.trialConvertedToSubscription,
		featureFlags,
		isFetchingFeatureFlags,
		featureFlagsFetchError,
		activeLicense,
		trialInfo,
		isCloudUser,
		isEnterpriseSelfHostedUser,
	]);

	useEffect(() => {
		if (!isFetchingUser && isCloudUser && user && user.email) {
			enableAnalytics(user);
		}
	}, [user, isFetchingUser, isCloudUser, enableAnalytics]);

	useEffect(() => {
		if (isCloudUser || isEnterpriseSelfHostedUser) {
			if (
				window.signozBootData?.settings?.posthog?.enabled &&
				window.signozBootData?.settings?.posthog?.key
			) {
				posthog.init(window.signozBootData.settings.posthog.key, {
					api_host: window.signozBootData.settings.posthog.apiHost,
					ui_host: window.signozBootData.settings.posthog.uiHost,
					person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
				});
			}

			if (
				!isSentryInitialized &&
				window.signozBootData?.settings?.sentry?.enabled
			) {
				Sentry.init({
					dsn: window.signozBootData.settings.sentry.dsn,
					tunnel: window.signozBootData.settings.sentry.tunnel,
					environment: process.env.ENVIRONMENT,
					release: process.env.VERSION,
					integrations: [
						// Kept for the `transaction` tag used in routing, even though
						// tracing is disabled. Ref: https://github.com/SigNoz/platform-pod/issues/2393#issuecomment-4603658055
						Sentry.browserTracingIntegration(),
						Sentry.replayIntegration({
							maskAllText: false,
							blockAllMedia: false,
						}),
					],
					tracesSampleRate: 0, // Ref: https://github.com/SigNoz/platform-pod/issues/2393#issuecomment-4603658055
					replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
					replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
					beforeSend(event) {
						// Drop the event if its level is 'warning' or 'info'
						if (event.level === 'warning' || event.level === 'info') {
							return null;
						}

						const sessionReplayUrl = posthog.get_session_replay_url?.({
							withTimestamp: true,
						});
						if (sessionReplayUrl) {
							// eslint-disable-next-line no-param-reassign
							event.contexts = {
								...event.contexts,
								posthog: { session_replay_url: sessionReplayUrl },
							};
						}
						return event;
					},
				});

				setIsSentryInitialized(true);
			}
		} else {
			posthog.reset();
			Sentry.close();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCloudUser, isEnterpriseSelfHostedUser]);

	if (isPreflightLoading) {
		return <Spinner tip="Loading..." />;
	}

	// if the user is in logged in state
	if (isLoggedInState) {
		// if the setup calls are loading then return a spinner
		if (isFetchingActiveLicense || isFetchingUser || isFetchingFeatureFlags) {
			return <AppLoading />;
		}

		// if the required calls fails then return a something went wrong error
		// this needs to be on top of data missing error because if there is an error, data will never be loaded and it will
		// move to indefinitive loading
		if (userFetchError && pathname !== ROUTES.SOMETHING_WENT_WRONG) {
			history.replace(ROUTES.SOMETHING_WENT_WRONG);
		}

		// if all of the data is not set then return a spinner, this is required because there is some gap between loading states and data setting
		if (
			(!activeLicense || !user.email || !featureFlags) &&
			!userFetchError &&
			!activeLicenseFetchError
		) {
			return <Spinner tip="Loading..." />;
		}
	}

	return (
		<Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />}>
			<ConfigProvider theme={themeConfig}>
				<Router history={history}>
					<CompatRouter>
						<CmdKProvider>
							<NotificationProvider>
								<ErrorModalProvider>
									{isLoggedInState && <CmdKPalette userRole={user.role} />}
									{isLoggedInState && (
										<ShiftHoldOverlayController userRole={user.role} />
									)}
									<PrivateRoute>
										<ResourceProvider>
											<QueryBuilderProvider>
												<KeyboardHotkeysProvider>
													<AppLayout>
														<PreferenceContextProvider>
															<Suspense fallback={<Spinner size="large" tip="Loading..." />}>
																<Switch>
																	{routes.map(({ path, component, exact }) => (
																		<Route
																			key={`${path}`}
																			exact={exact}
																			path={path}
																			component={component}
																		/>
																	))}
																	<Route exact path="/" component={Home} />
																	<Route path="*" component={NotFound} />
																</Switch>
															</Suspense>
														</PreferenceContextProvider>
													</AppLayout>
												</KeyboardHotkeysProvider>
											</QueryBuilderProvider>
										</ResourceProvider>
									</PrivateRoute>
								</ErrorModalProvider>
							</NotificationProvider>
						</CmdKProvider>
					</CompatRouter>
				</Router>
			</ConfigProvider>
		</Sentry.ErrorBoundary>
	);
}

export default App;
